import { useEffect, useRef } from 'react';

/**
 * Publishes the on-screen keyboard's height to CSS as `--kb-inset`.
 *
 * <h3>Why this exists</h3>
 * Android Chrome only resizes the layout viewport when the keyboard opens if the viewport
 * meta tag sets `interactive-widget=resizes-content`. This app ships the CRA boilerplate
 * meta tag, which does not. So on a Tecno Spark — the class of device field agents actually
 * carry — the keyboard slides over the page and the composer ends up underneath it, with no
 * layout change to react to.
 *
 * Rather than depend on changing a meta tag that affects every screen in the app, this reads
 * `window.visualViewport`, which reports the ACTUALLY VISIBLE region, and writes the
 * difference to a custom property the composer pads itself by.
 *
 * <h3>Why not scrollIntoView</h3>
 * The obvious fix — scroll the composer into view on focus — fights the browser's own
 * scroll-anchoring and produces a visible jump-and-settle. Padding the layout means the
 * composer was never off-screen to begin with.
 *
 * @param elementRef the panel root the property is written to, so the effect is scoped to
 *        this surface rather than leaking onto `document.documentElement`.
 * @param onInsetChange called after each change, so a pinned transcript can re-pin in the
 *        same frame instead of being pushed up behind the keyboard.
 */
export const useKeyboardInset = (
	elementRef: React.RefObject<HTMLElement | null>,
	onInsetChange?: (inset: number) => void
): void => {
	const lastInset = useRef(0);
	const onChangeRef = useRef(onInsetChange);
	onChangeRef.current = onInsetChange;

	useEffect(() => {
		const viewport = typeof window !== 'undefined' ? window.visualViewport : undefined;
		const element = elementRef.current;

		// jsdom has no visualViewport, and neither do older Android WebViews. Both degrade to
		// zero inset, which is exactly the pre-existing behaviour — never a broken layout.
		if (!viewport || !element) return;

		const apply = () => {
			// The keyboard's height is what the visual viewport lost relative to the layout
			// viewport, minus however far the page itself is scrolled within it.
			const raw = window.innerHeight - viewport.height - viewport.offsetTop;

			// Small deltas are browser chrome (the URL bar collapsing), not a keyboard.
			// Treating those as an inset makes the composer twitch during ordinary scrolling.
			const inset = raw > 80 ? Math.round(raw) : 0;

			if (inset === lastInset.current) return;
			lastInset.current = inset;

			element.style.setProperty('--kb-inset', `${inset}px`);
			onChangeRef.current?.(inset);
		};

		apply();
		viewport.addEventListener('resize', apply);
		viewport.addEventListener('scroll', apply);

		return () => {
			viewport.removeEventListener('resize', apply);
			viewport.removeEventListener('scroll', apply);
			element.style.removeProperty('--kb-inset');
		};
	}, [elementRef]);
};

/**
 * The inset calculation, extracted so it can be asserted without a browser.
 *
 * Exported for tests: jsdom provides no `visualViewport`, so the hook itself is a no-op
 * there and the arithmetic — which is the part that can actually be wrong — would otherwise
 * go uncovered.
 */
export const keyboardInsetFrom = (innerHeight: number, viewportHeight: number, offsetTop: number): number => {
	const raw = innerHeight - viewportHeight - offsetTop;
	return raw > 80 ? Math.round(raw) : 0;
};
